import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { GoogleMap, MapInfoWindow } from '@angular/google-maps';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CredentialService } from 'src/app/services/core/credential/credential.service';
import { SharedDataService } from 'src/app/services/app/shared-service/shared-data.service';
import { timeZone } from 'src/app/utils/utils.common';
import * as moment from 'moment-timezone';
import { forkJoin } from 'rxjs';
import { NearbyService } from 'src/app/services/core/nearby-service/nearby.service';
import { TitleCasePipe } from '@angular/common';
import { SocketService } from 'src/app/services/app/socket-service/socket-service.service';
import { Events } from 'src/app/utils/event-utils';


@UntilDestroy()
@Component({
  selector: 'app-map-dashboard',
  templateUrl: './map-dashboard.component.html',
  styleUrls: ['./map-dashboard.component.css']
})
export class MapDashboardComponent implements OnInit, AfterViewInit {
  @ViewChild(GoogleMap) map!: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;

  infoWindowRef: google.maps.InfoWindow | null = null;
  center: google.maps.LatLngLiteral = { lat: 37.7749, lng: -122.4194 };
  zoom = 5;
  // allMarkers = new google.maps.MVCArray<google.maps.marker.AdvancedMarkerElement>();
  allMarkers = new Map<string, google.maps.marker.AdvancedMarkerElement>();

  user: any;
  timeZone = timeZone;
  todayDate: any = [
    moment().tz(this.timeZone).startOf("day").toISOString(),
    moment().tz(this.timeZone).endOf("day").toISOString()
  ];

  companies: any[] = [];
  preCompanies: any = {};
  selectedCompany: any = '';
  type: 'start' | 'gps' = 'start';
  allDrivers: any[] = [];
  drivers: any[] = [];
  points: any[] = [];
  selectedDriver: any;
  search = '';
  filteredDrivers:any = [];

  constructor(
    private nearbyService: NearbyService,
    private credentialService: CredentialService,
    private sharedDataService: SharedDataService,
    private socketService: SocketService
  ) { }

  ngOnInit() {
    this.getUser();
    this.listenSocket();
  }

  listenSocket() {
    this.socketService
      .addListener(Events.DRIVER_LOCATION_UPDATE_GLOBAL)
      .pipe(untilDestroyed(this)).subscribe(res => {
        if (res && res.data) {
          this.updateDriverMapLocation(res.data);
        }
      });
  }

  ngAfterViewInit(): void {
    this.map.googleMap?.setOptions({
      mapId: '9fe6e06d06a9024ce7fa85b2',
    });
  }

  /*** MAIN DATA FLOW ***/
  getUser() {
    this.sharedDataService.getUser()
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        if (data) {
          this.user = data;
          if (this.user.timeZone) this.timeZone = this.user.timeZone;
          this.todayDate = [
            moment().tz(this.timeZone).startOf("day").toISOString(),
            moment().tz(this.timeZone).endOf("day").toISOString()
          ];
          this.loadInitialData();
        }
      });
  }

  loadInitialData() {
    const queryparams = { limit: 9999, isApproved: true, isActive: true };
    forkJoin({
      companiesRes: this.credentialService.getPicker(queryparams),
      driversRes: this.credentialService.getPickerDrivers(queryparams)
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ companiesRes, driversRes }: any) => {
        if (companiesRes?.results?.length) {
          this.companies = companiesRes.results;
          this.preCompanies = companiesRes.results.reduce((obj: any, item: any) => (obj[item.id] = item, obj), {});
        } else {
          this.companies = [];
          this.preCompanies = [];
        }

        if (driversRes?.results?.length) {
          this.allDrivers = driversRes.results;
          this.filteredDrivers = driversRes.results
        } else {
          this.filteredDrivers = [];
          this.allDrivers = [];
        }

        this.getDriverStartPoints();
      });
  }

  getPoints(type: 'start' | 'gps') {
    this.type = type;
    this.search = '';
    this.selectedCompany = '';
    this.clearMarkers();
    if (type === 'start') {
      this.getDriverStartPoints();
    } else {
      this.getDriverGpsPoints();
    }
  }

  getDriverStartPoints() {
    this.drivers = this.allDrivers;
    if (this.selectedCompany) {
      this.filteredDrivers = this.allDrivers.filter((d: any) => d.company === this.selectedCompany);
    } else {
      this.filteredDrivers = this.allDrivers;
    }
    this.points = this.filteredDrivers;
    this.addMarkers(this.points);
  }
  

  getDriverGpsPoints() {
    const queryParams = { time: [this.todayDate[0], this.todayDate[1]] };
    this.nearbyService.getOnline(queryParams)
      .pipe(untilDestroyed(this))
      .subscribe((data: any) => {
        if (data && data.length) {
          const extracted = data.map(({ driver, ...rest }: { driver: any;[key: string]: any }) => ({
            ...rest,
            ...driver
          }));

          this.drivers = extracted;
          if (this.selectedCompany) {
            this.filteredDrivers = extracted.filter((d:any) => d.company === this.selectedCompany);
          } else {
            this.filteredDrivers = extracted;
          }
          this.points = this.filteredDrivers;
          this.addMarkers(this.points);
        } else {
          this.filteredDrivers = [];
          this.drivers = [];
          this.points = [];
          this.addMarkers(this.points);
        }
      });
  }

  addMarkers(points: any[]) {
    const bounds = new google.maps.LatLngBounds();
    if (!points?.length) return;
    points.forEach((point: any) => {

      let coords = null;
      let title = point.firstName +" "+ point.lastName;
      
      if (this.type === 'start') {
        coords = point.address?.coords?.length
          ? { lat:  point.address.coords[1], lng: point.address.coords[0] }
          : null;
      } else {

        coords = point?.location?.coordinates?.length
          ? { lat: point.location.coordinates[1], lng: point.location.coordinates[0] }
          : null;
      }
      
      if (!coords) return;

     bounds.extend(new google.maps.LatLng(coords));

      const markerElement = document.createElement('div');
      markerElement.innerHTML = this.getMarkerContent(point);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map.googleMap,
        position: coords,
        content: markerElement,
        title
      });

      // this.allMarkers.push(marker);
      this.allMarkers.set(point._id, marker);

      const infoWindow = new google.maps.InfoWindow({
        content: this.getInfoWindowContent(point)
      });

      marker.addListener('click', () => {
        if (this.infoWindowRef) this.infoWindowRef.close();
        this.infoWindowRef = infoWindow;
        infoWindow.open({ anchor: marker, map: this.map.googleMap });
      });
    });
    this.map.googleMap?.fitBounds(bounds);
  }

  updateDriverMapLocation(data: any) {
    if (this.type === 'gps' && this.filteredDrivers && this.filteredDrivers.length) {
      const {driver, location} = data;
      const marker = this.allMarkers.get(driver);
      if (!marker) return;
      const coords = location?.coordinates?.length
          ? { lat: location.coordinates[1], lng: location.coordinates[0] }
          : null;
      
      if (!coords) return;
      marker.position = coords;
    }
  }

  clearMarkers() {
    this.allMarkers.forEach(marker => (marker.map = null));
    this.allMarkers.clear();
  }


  onSelectDriver(driver: any) {
    this.clearMarkers();
    if (driver._id) {
      driver.id = driver._id;
    }
    if (this.selectedDriver !== driver) {
      this.selectedDriver = driver;
      this.points = [this.selectedDriver];
      this.addMarkers(this.points);
    } else {
      this.selectedDriver = null;
      if (this.type === 'start') {
        this.getDriverStartPoints();
      } else {
        this.getDriverGpsPoints();
      }
    }
  }

  onSelectCompany(companyId: string) {
    this.selectedCompany = companyId;
    this.clearMarkers();

    if (!companyId) {
      this.filteredDrivers = ( this.type ==="start" ? this.allDrivers: this.drivers);
      this.points = this.filteredDrivers;
    } else {
      this.filteredDrivers =  ( this.type ==="start" ? this.allDrivers: this.drivers).filter(d => d.company === companyId);
      this.points = this.filteredDrivers;
    }

    this.addMarkers(this.points);
  }


  getMarkerContent(entity: any): string {
    let title = new TitleCasePipe().transform(entity.firstName + " " + entity.lastName);
    const size = entity.company ? 'w-10 h-10' : 'w-12 h-12';
    return `
      <div>
        <div class="${size} rounded-full overflow-hidden border-2 border-gray-300 shadow-md">
          <img src="${entity.profileImageURL || 'assets/images/profileImagePlaceholder.png'}"
               alt="${title}" class="w-full h-full object-cover"/>
        </div>
      </div>`;
  }

  getInfoWindowContent(entity: any): string {
     let title = new TitleCasePipe().transform(entity.firstName +" "+ entity.lastName)
    let companyName =
      entity.company && this.preCompanies[entity.company]
        ? new TitleCasePipe().transform(this.preCompanies[entity.company].name)
        : '';
    return `
      <div>
        <p><span class="font-semibold">Name:</span> ${title}</p>
        <p><span class="font-semibold">Email:</span> ${entity.email}</p>
        ${
        companyName
          ? `<p><span class="font-semibold">Company:</span> ${companyName}</p>`
          : ''
      }
         <p><span class="font-semibold">Lat:</span> ${entity?.address?.coords?.[0] || entity?.location?.coordinates?.[0] || 'N/A'}</p>
         <p><span class="font-semibold">Lng:</span> ${entity?.address?.coords?.[1] || entity?.location?.coordinates?.[1] || 'N/A'}</p>
      </div>`;
  }
}
